import { useCallback, useState } from 'react'
import {
  GetDepartmentHeadForCaseQuery,
  IsDepartmentHeadForCaseQuery,
  useApproveBillByAttorneyMutation,
  useApproveBillByDepartmentHeadMutation,
  useArchiveBillMutation,
  useGetDepartmentHeadForCaseLazyQuery,
  useIsDepartmentHeadForCaseLazyQuery,
  useSendBillToAttorneyMutation,
  useSendBillToClientMutation,
  useSendReminderToClientMutation,
  useVoidBillMutation
} from '../../generated'
import { Alert } from '../components/common/Alert'

interface UseBillActionsProps {
  onRefetch: () => void
  onClose?: () => void
}

export const useBillActions = ({ onRefetch, onClose }: UseBillActionsProps) => {
  const [departmentHead, setDepartmentHead] =
    useState<GetDepartmentHeadForCaseQuery['getDepartmentHeadForCase']['departmentHead']>()
  const [isDepartmentHead, setIsDepartmentHead] =
    useState<IsDepartmentHeadForCaseQuery['isDepartmentHeadForCase']['isDepartmentHead']>()
  const [showVoidConfirmation, setShowVoidConfirmation] = useState(false)
  const [voidBlockedMessage, setVoidBlockedMessage] = useState<string | null>(null)
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false)
  const [showWriteOffConfirmation, setShowWriteOffConfirmation] = useState(false)
  const [showAdminApprovalConfirmation, setShowAdminApprovalConfirmation] = useState(false)

  const [fetchDepartmentHeadForCase] = useGetDepartmentHeadForCaseLazyQuery({
    onCompleted: data => {
      setDepartmentHead(data.getDepartmentHeadForCase.departmentHead)
    }
  })

  const [isDepartmentHeadForCase] = useIsDepartmentHeadForCaseLazyQuery({
    onCompleted: data => {
      if (data.isDepartmentHeadForCase.isDepartmentHead) {
        setIsDepartmentHead(data.isDepartmentHeadForCase.isDepartmentHead)
      }
    }
  })

  const [sendBillToAttorney, { loading: sendingForApproval }] = useSendBillToAttorneyMutation({
    onCompleted: data => {
      if (data?.sendBillToAttorney?.response?.status === 200) {
        Alert.success('Bill sent for approval to responsible attorney')
        onClose?.()
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to send bill for approval')
    }
  })

  const [approveBillByAttorney, { loading: approvingByAttorney }] = useApproveBillByAttorneyMutation({
    onCompleted: data => {
      if (data?.approveBillByAttorney?.response?.status === 200) {
        Alert.success('Bill approved successfully')
        onClose?.()
        setShowAdminApprovalConfirmation(false)
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to approve bill')
      onRefetch()
    }
  })

  const [approveBillByDepartmentHead, { loading: approvingByDepartmentHead }] = useApproveBillByDepartmentHeadMutation({
    onCompleted: data => {
      if (data?.approveBillByDepartmentHead?.response?.status === 200) {
        Alert.success('Bill approved successfully')
        onClose?.()
        setShowAdminApprovalConfirmation(false)
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to approve bill')
    }
  })

  const [voidBill, { loading: voiding }] = useVoidBillMutation({
    onCompleted: data => {
      if (data?.voidBill?.response?.status === 200) {
        Alert.success('Bill voided successfully')
        setShowVoidConfirmation(false)
        onRefetch()
      }
    },
    onError: error => {
      setShowVoidConfirmation(false)

      // The server does the authoritative check (the UI can be looking at a bill whose payment
      // allocations weren't loaded). Surface its refusal in the same explanatory modal instead of
      // a toast the user has to interpret.
      const message = error.message || 'Failed to void bill'
      if (message.toLowerCase().includes('cannot be voided')) {
        setVoidBlockedMessage(message)
        return
      }

      Alert.error(message)
    }
  })

  const [archiveBill, { loading: archiving }] = useArchiveBillMutation({
    onCompleted: data => {
      if (data?.archiveBill?.response?.status === 200) {
        Alert.success('Bill archived successfully')
        onClose?.()
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to archive bill')
    }
  })

  const [sendBillToClient, { loading: sendingToClient }] = useSendBillToClientMutation({
    onCompleted: data => {
      if (data?.sendBillToClient?.response?.status === 200) {
        Alert.success('Bill sent to client successfully')
        onClose?.()
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to send bill to client')
    }
  })

  const [sendReminderToClient, { loading: sendingReminderToClient }] = useSendReminderToClientMutation({
    onCompleted: data => {
      const { sendReminderToClient } = data || {}
      const { response } = sendReminderToClient || {}
      if (response?.status === 200) {
        Alert.success(response?.message || '')
        onClose?.()
        onRefetch()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to send reminder to client')
    }
  })

  const handleSendForApproval = (billId: number) => {
    sendBillToAttorney({
      variables: {
        sendBillToAttorneyInput: { billId }
      }
    })
  }

  const handleApproveByAttorney = (billId: number) => {
    approveBillByAttorney({
      variables: {
        approveBillByAttorneyInput: { billId }
      }
    })
  }

  const handleApproveByDepartmentHead = (billId: number) => {
    approveBillByDepartmentHead({
      variables: {
        approveBillByDepartmentHeadInput: { billId }
      }
    })
  }

  const handleVoidBill = (billId: number) => {
    voidBill({
      variables: {
        voidBillInput: {
          billId
        }
      }
    })
  }

  const fetchDepartmentHead = useCallback((caseId: string) => {
    fetchDepartmentHeadForCase({
      variables: { caseId }
    })
  }, [fetchDepartmentHeadForCase])

  const checkIsUserDepartmentHeadForCase = useCallback((caseId: string, userId: string) => {
    isDepartmentHeadForCase({
      variables: {
        fetchDepartmentHeadInput: {
          caseId,
          userId
        }
      }
    })
  }, [isDepartmentHeadForCase])

  const handleArchiveBill = (billId: number) => {
    archiveBill({
      variables: { archiveBillInput: { billId } }
    })
  }

  const handleSendBillToClient = (billId: number) => {
    sendBillToClient({
      variables: { sendBillToClientInput: { billId } }
    })
  }

  const handleSendingReminderToClient = (billId: number) => {
    sendReminderToClient({
      variables: { sendReminderToClientInput: { billId } }
    })
  }

  return {
    // State
    departmentHead,
    isDepartmentHead,
    showVoidConfirmation,
    setShowVoidConfirmation,
    voidBlockedMessage,
    setVoidBlockedMessage,
    showRecordPaymentModal,
    setShowRecordPaymentModal,
    showWriteOffConfirmation,
    setShowWriteOffConfirmation,
    showAdminApprovalConfirmation,
    setShowAdminApprovalConfirmation,
    sendingForApproval,
    approvingByAttorney,
    approvingByDepartmentHead,
    voiding,
    archiving,
    sendingToClient,
    sendingReminderToClient,

    // Actions
    handleSendForApproval,
    handleApproveByAttorney,
    handleApproveByDepartmentHead,
    handleVoidBill,
    handleArchiveBill,
    handleSendBillToClient,
    fetchDepartmentHead,
    checkIsUserDepartmentHeadForCase,
    handleSendingReminderToClient
  }
}
